import MyLocationIcon from "@mui/icons-material/MyLocation";
import { useMediaQuery, useTheme } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styles from "./GeolocateCoachMark.module.css";

const STORAGE_KEY = "aurea:geolocate-coach-mark-dismissed";

type Layout = {
  top: number;
  left: number;
  width: number;
  placement: "above" | "below";
};

export default function GeolocateCoachMark(): React.ReactNode | null {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const [layout, setLayout] = useState<Layout | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const rafRef = useRef<number | null>(null);

  const config = useMemo(() => {
    if (isMobile) {
      return {
        width: 252,
        margin: 10,
        gap: 25,
      };
    }

    return {
      width: 270,
      margin: 10,
      gap: 30,
    };
  }, [isMobile]);

  const reposition = useCallback(() => {
    const btn = document.querySelector<HTMLButtonElement>(
      ".maplibregl-ctrl-geolocate",
    );
    const mapContainer = btn?.closest(".maplibregl-map") as HTMLElement | null;

    if (!btn || !mapContainer) return false;

    const mapRect = mapContainer.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();

    const width = Math.min(config.width, mapRect.width - config.margin * 2);
    const tooltipHeight =
      tooltipRef.current?.offsetHeight ?? (isMobile ? 160 : 158);

    const centerX = btnRect.left - mapRect.left + btnRect.width / 2;
    const maxLeft = Math.max(
      config.margin,
      mapRect.width - config.margin - width,
    );
    const left = Math.min(
      Math.max(centerX - width / 2, config.margin),
      maxLeft,
    );

    const aboveTop = btnRect.top - mapRect.top - tooltipHeight - config.gap;
    const belowTop = btnRect.bottom - mapRect.top + config.gap;
    const fitsAbove = aboveTop >= config.margin;
    const fitsBelow =
      belowTop + tooltipHeight <= mapRect.height - config.margin;

    let top = aboveTop;

    if (!fitsAbove && fitsBelow) {
      top = belowTop;
    }

    top = Math.min(
      Math.max(top, config.margin),
      Math.max(config.margin, mapRect.height - config.margin - tooltipHeight),
    );

    setLayout({
      top,
      left,
      width,
      placement: fitsAbove || !fitsBelow ? "above" : "below",
    });

    return true;
  }, [config, isMobile]);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;

    const scheduleReposition = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        reposition();
      });
    };

    const attachObservers = () => {
      const btn = document.querySelector<HTMLButtonElement>(
        ".maplibregl-ctrl-geolocate",
      );
      const mapContainer = btn?.closest(
        ".maplibregl-map",
      ) as HTMLElement | null;

      if (!btn || !mapContainer) return false;

      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = new ResizeObserver(scheduleReposition);
      resizeObserverRef.current.observe(mapContainer);
      resizeObserverRef.current.observe(btn);
      if (tooltipRef.current) {
        resizeObserverRef.current.observe(tooltipRef.current);
      }

      window.addEventListener("resize", scheduleReposition);
      window.addEventListener("orientationchange", scheduleReposition);
      window.visualViewport?.addEventListener("resize", scheduleReposition);
      return true;
    };

    const cleanup = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
      mutationObserverRef.current?.disconnect();
      resizeObserverRef.current?.disconnect();
      window.removeEventListener("resize", scheduleReposition);
      window.removeEventListener("orientationchange", scheduleReposition);
      window.visualViewport?.removeEventListener("resize", scheduleReposition);
    };

    if (attachObservers()) {
      scheduleReposition();
      return cleanup;
    }

    mutationObserverRef.current = new MutationObserver(() => {
      if (attachObservers()) {
        mutationObserverRef.current?.disconnect();
        scheduleReposition();
      }
    });

    mutationObserverRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return cleanup;
  }, [layout, reposition]);

  const dismiss = useCallback(() => {
    setLayout(null);
    localStorage.setItem(STORAGE_KEY, "true");
  }, []);

  if (!layout) return null;

  return (
    <>
      <div
        className={styles.scrim}
        onClick={dismiss}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            dismiss();
          }
        }}
        role="presentation"
      />

      <Spotlight />

      <div
        ref={tooltipRef}
        className={styles.tooltip}
        data-placement={layout.placement}
        style={{
          top: `${layout.top}px`,
          left: `${layout.left}px`,
          width: `${layout.width}px`,
        }}
      >
        <div className={styles.header}>
          <span className={styles.icon} aria-hidden="true">
            <MyLocationIcon sx={{ fontSize: { xs: 15, md: 16 } }} />
          </span>
          <strong className={styles.title}>
            {t("coachMark.geolocate.title", "Find your location")}
          </strong>
        </div>

        <p className={styles.description}>
          {t(
            "coachMark.geolocate.description",
            "Tap this button to center the map on your current location.",
          )}
        </p>

        <div className={styles.footer}>
          <button type="button" className={styles.dismissBtn} onClick={dismiss}>
            {t("coachMark.geolocate.dismiss", "Got it")}
          </button>
        </div>

        <span className={styles.arrow} />
      </div>
    </>
  );
}

function Spotlight(): React.ReactNode {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const ref = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const updateSpotlight = useCallback(() => {
    const btn = document.querySelector<HTMLButtonElement>(
      ".maplibregl-ctrl-geolocate",
    );
    const mapContainer = btn?.closest(".maplibregl-map") as HTMLElement | null;
    if (!btn || !mapContainer || !ref.current) return;

    const mapRect = mapContainer.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const size = Math.max(btnRect.width, btnRect.height) + (isMobile ? 14 : 16);

    Object.assign(ref.current.style, {
      width: `${size}px`,
      height: `${size}px`,
      top: `${btnRect.top - mapRect.top + btnRect.height / 2 - size / 2}px`,
      left: `${btnRect.left - mapRect.left + btnRect.width / 2 - size / 2}px`,
    });
  }, [isMobile]);

  useEffect(() => {
    const schedule = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateSpotlight);
    };

    const btn = document.querySelector<HTMLButtonElement>(
      ".maplibregl-ctrl-geolocate",
    );
    const mapContainer = btn?.closest(".maplibregl-map") as HTMLElement | null;

    resizeObserverRef.current?.disconnect();
    if (btn && mapContainer) {
      resizeObserverRef.current = new ResizeObserver(schedule);
      resizeObserverRef.current.observe(mapContainer);
      resizeObserverRef.current.observe(btn);
    }

    schedule();
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    window.visualViewport?.addEventListener("resize", schedule);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
      resizeObserverRef.current?.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
    };
  }, [updateSpotlight]);

  return <div ref={ref} className={styles.spotlight} />;
}
